import { useState, useContext, useEffect, useRef, useMemo, Fragment } from 'react'
import { TableContext } from 'Context/Table'
import DoubleLine from 'Presentation/Components/Table/Cells/DoubleLine'
import { useLang } from '_metronic/i18n/Metronici18n'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useProfileStore } from 'Store/Profile'
import { useMembershipStore } from 'Store/Membership'
import { shallow } from 'zustand/shallow'
import formatNumberCommas from 'Utils/formatNumberCommas'
import moment from 'moment'
import { TABS } from '../Config'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

const VIEW_INDEX = 7

const MOCK_DATA = [
	{
		planName: 'Enterprise Plan',
		planDescription: 'This is a enterprise plan',
		createdAt: moment().toISOString(),
		net: 4999.0,
	},
	{
		planName: 'Business Plan',
		planDescription: 'This is a business plan',
		createdAt: moment().subtract(1, 'months').toISOString(),
		net: 2999.0,
	},
	{
		planName: 'Business Plan',
		planDescription: 'This is a business plan',
		createdAt: moment().subtract(3, 'months').toISOString(),
		net: 2999.0,
	},
	{
		planName: 'Standard Plan',
		planDescription: 'This is a standard plan',
		createdAt: moment().subtract(4, 'months').toISOString(),
		net: 0,
	},
]

const INITIAL_PROMPT_PAY_DATA = {
	qrImage: '' as string | null,
	qrExpireTime: '' as string | null,
	paymentId: '' as string | null,
}

const ViewModel = () => {
	const lang = useLang()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const paymentStatus = searchParams.get('paymentStatus')
	const tradeStatus = searchParams.get('tradeStatus')
	const profile = useProfileStore(state => state.profile, shallow)
	const {
		updateLoading,
		updateError,
		updatePagination,
		updateSorting,
		resetState: clearStateTable,
	} = useContext(TableContext)

	const { rawData, getAll, checkPromptPayResult, purchase } = useMembershipStore(
		state => ({
			rawData: state.data,
			getAll: state.getAll,
			purchase: state.purchase,
			checkPromptPayResult: state.checkPromptpayResult,
			clearState: state.clearState,
		}),
		shallow
	)

	const [isSubmitting, setIsSubmitting] = useState(false)
	const [isOpenPaymentMethod, setIsOpenPaymentMethod] = useState(false)
	const [planPrice, setPlanPrice] = useState(0)
	const [planUUID, setPlanUUID] = useState('')
	const [promptPayData, setPromptPayData] = useState(INITIAL_PROMPT_PAY_DATA)
	const [currentPlan, setCurrentPlan] = useState<{
		slug: string
		remainingDays: number
	}>({
		slug: '',
		remainingDays: 0,
	})
	const [isYear, setIsYear] = useState(false)

	const checkPromptPayIntervalRef = useRef<NodeJS.Timeout | null>(null)

	const plans = useMemo(
		() =>
			rawData
				.map(item => ({
					...item,
				}))
				.filter(item => (isYear ? item.durationDay === 365 : item.durationDay === 31)),
		[rawData, isYear]
	)

	const findTabPath = (tabIndex: number) => {
		const tab = TABS.find((_, idx) => idx === tabIndex)
		if (tab) {
			return tab.path
		}
	}

	const onChangeTab = (tabIndex: number) => {
		if (tabIndex !== VIEW_INDEX) {
			navigate(findTabPath(tabIndex) ?? '#')
		}
	}

	const setupTable = () => {
		updatePagination(true)
		updateSorting('createdAt', true)
	}

	const fetchData = () => {
		updateLoading(true)
		getAll().then(response => {
			setCurrentPlan(
				profile?.currentPackage ?? {
					slug: 'standard',
					remainingDays: 0,
				}
			)

			if (!response.success) {
				toast.error(response.data ?? 'An error occurred')
			} else {
				updateLoading(false)
				updateError(false)
			}
		})
	}

	const onPurchase = (uuid: string) => {
		const findedPlan = plans.find(item => item.uuid === uuid)

		withReactContent(Swal)
			.fire({
				icon: 'question',
				title: lang === 'en' ? 'Subscribe a plan' : 'สมัครแพ็คเกจ',
				html:
					lang === 'en' ? (
						<Fragment>
							Would you like to subscribe a plan{' '}
							<div className="fs-5 fw-bold text-kumopack-primary-600">{findedPlan?.name}</div> for{' '}
							{formatNumberCommas(findedPlan?.price ?? 0)} THB
						</Fragment>
					) : (
						<Fragment>
							คุณต้องการสมัครแพ็คเกจ{' '}
							<div className="fs-5 fw-bold text-kumopack-primary-600">{findedPlan?.name}</div>{' '}
							ในราคา {formatNumberCommas(findedPlan?.price ?? 0)} บาท นี้หรือไม่
						</Fragment>
					),
				confirmButtonText: lang === 'en' ? 'Confirm' : 'ยืนยัน',
				cancelButtonText: lang === 'en' ? 'Cancel' : 'ยกเลิก',
				showCancelButton: true,
				showConfirmButton: true,
				reverseButtons: true,
				customClass: {
					confirmButton: 'btn btn-kumopack-primary-600 text-kumopack-base-white',
					cancelButton: 'btn btn-muted btn-active-light',
				},
			})
			.then(result => {
				if (result.isConfirmed) {
					setPlanUUID(uuid)
					setPlanPrice(plans.find(item => item.uuid === uuid)?.price ?? 0)
					setIsOpenPaymentMethod(true)
				}
			})
	}

	const handleCheckPromptPay = async (paymentId: string) => {
		return checkPromptPayResult(paymentId).then(({ success, data }) => {
			if (success) {
				return data?.paymentStatus ?? false
			} else {
				return false
			}
		})
	}

	const onPurchaseConfirmed = async (
		paymentMethod: 'bank_transfer' | 'credit-card' | 'promptpay',
		evidence?: File | undefined
	) => {
		setIsSubmitting(true)

		const formData = new FormData()
		formData.append('packageUuid', planUUID)
		formData.append('paymentMethod', paymentMethod)

		return purchase(formData).then(({ success, data }) => {
			if (!success) {
				if (paymentMethod === 'credit-card' || paymentMethod === 'promptpay') {
					setIsOpenPaymentMethod(false)
				}

				Swal.fire({
					icon: 'error',
					title: 'ชำระเงินไม่สำเร็จ',
					text: data,
					confirmButtonText: 'ปิด',
					customClass: {
						confirmButton: 'btn btn-kumopack-primary-600 text-kumopack-base-white',
					},
				}).then(() => {
					setIsSubmitting(false)
				})

				return false
			} else {
				const { data: responseData } = data

				if (paymentMethod === 'credit-card') {
					const { redirectUrl } = responseData
					window.location.href = redirectUrl
				} else if (paymentMethod === 'promptpay') {
					const { qrImage, qrExpireTime, paymentId } = responseData

					setIsSubmitting(false)
					setPromptPayData({
						qrImage,
						qrExpireTime,
						paymentId,
					})
					checkPromptPayIntervalRef.current = setInterval(() => {
						handleCheckPromptPay(paymentId).then(status => {
							if (status) {
								setIsSubmitting(false)
								fetchData()
								setIsOpenPaymentMethod(false)

								Swal.fire({
									icon: 'success',
									title: lang === 'en' ? 'Payment successful' : 'ชำระเงินสำเร็จ',
									text:
										lang === 'en'
											? 'You have successfully subscribed to the package'
											: 'คุณได้สมัครแพ็คเกจเรียบร้อยแล้ว',
									confirmButtonText: lang === 'en' ? 'Close' : 'ปิด',
									customClass: {
										confirmButton: 'btn btn-kumopack-primary-600 text-kumopack-base-white',
									},
								}).then(() => {
									clearInterval(checkPromptPayIntervalRef.current as NodeJS.Timeout)
								})
							}
						})
					}, 1000)
				}

				return true
			}
		})
	}

	const onPurchaseCancel = () => {
		setIsOpenPaymentMethod(false)
		setIsSubmitting(false)
		setPromptPayData(INITIAL_PROMPT_PAY_DATA)
		setPlanPrice(0)
		setPlanUUID('')
	}

	const handleAfterShowPaymentResult = () => {
		searchParams.delete('paymentStatus')
		searchParams.delete('tradeStatus')
		window.history.replaceState({}, '', window.location.pathname)
	}

	const handleShowPaymentResult = () => {
		if (paymentStatus && tradeStatus) {
			if (paymentStatus === 'SUCCESS') {
				Swal.fire({
					icon: 'success',
					title: lang === 'en' ? 'Payment successful' : 'ชำระเงินสำเร็จ',
					text:
						lang === 'en'
							? 'You have successfully subscribed to the package'
							: 'คุณได้สมัครแพ็คเกจเรียบร้อยแล้ว',
					confirmButtonText: lang === 'en' ? 'Close' : 'ปิด',
					customClass: {
						confirmButton: 'btn btn-kumopack-primary-600 text-kumopack-base-white',
					},
				}).then(() => {
					handleAfterShowPaymentResult()
				})
			} else {
				Swal.fire({
					icon: 'error',
					title: lang === 'en' ? 'Payment Failed' : 'ชำระเงินไม่สำเร็จ',
					text: lang === 'en' ? 'Your payment has failed' : 'การชำระเงินของคุณไม่สำเร็จ',
					confirmButtonText: lang === 'en' ? 'Close' : 'ปิด',
					customClass: {
						confirmButton: 'btn btn-kumopack-primary-600 text-kumopack-base-white',
					},
				}).then(() => {
					handleAfterShowPaymentResult()
				})
			}
		}
	}

	const TABLE_CONFIGS: any[] = [
		{
			Header: 'Description',
			width: 200,
			minWidth: 200,
			accessor: 'plan',
			Cell: ({ row }: any) => (
				<DoubleLine label={row.original.planName} description={row.original.planDescription} />
			),
		},
		{
			Header: 'Date',
			width: 120,
			minWidth: 120,
			accessor: 'createdAt',
			Cell: ({ value }: any) => (
				<span className="text-kumopack-grey-500">{moment(value).format('DD/MM/YYYY')}</span>
			),
		},
		{
			Header: 'Total',
			width: 120,
			minWidth: 120,
			accessor: 'net',
			Cell: ({ value }: any) => (
				<div className="d-flex justfiy-content-end">
					<span className="text-kumopack-grey-500">{formatNumberCommas(value)}</span>
				</div>
			),
		},
		{
			Header: '',
			width: 120,
			minWidth: 120,
			accessor: 'action',
			Cell: ({ row }: any) => (
				<div className="d-flex align-items-center justify-content-end">
					{row.original.net > 0 && (
						<div className="w-100">
							<button
								className="btn btn-sm btn-muted btn-active-light text-kumopack-primary-700 text-start"
								onClick={() => {}}>
								Download Tax Invoice
							</button>
						</div>
					)}
				</div>
			),
		},
	]

	useEffect(() => {
		fetchData()
		// eslint-disable-next-line
	}, [])

	useEffect(() => {
		setupTable()

		return () => {
			clearStateTable()
		}
		// eslint-disable-next-line
	}, [])

	useEffect(() => {
		if (!isOpenPaymentMethod) {
			if (checkPromptPayIntervalRef.current) {
				clearInterval(checkPromptPayIntervalRef.current)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpenPaymentMethod])

	useEffect(() => {
		handleShowPaymentResult()
		// eslint-disable-next-line
	}, [paymentStatus, tradeStatus])

	return {
		currentPlan,
		companyName: profile?.companyName ?? '',
		currentTab: VIEW_INDEX,
		onChangeTab,
		data: MOCK_DATA,
		TABLE_CONFIGS,
		isYear,
		plans,
		promptPayData,
		isOpenPaymentMethod,
		isSubmitting,
		planPrice,
		setIsYear,
		onPurchase,
		onPurchaseConfirmed,
		onPurchaseCancel,
	}
}

export default ViewModel
